import { Card } from '@/components/ui/Card'
import { type Product } from '@/constants/products'
import { CheckCircle2, ArrowRight } from 'lucide-react'

interface ProductBentoGridProps {
  product: Product
}

export function ProductBentoGrid({ product }: ProductBentoGridProps) {
  const { bento } = product

  return (
    <section className="py-12 sm:py-16 border-b border-border-subtle">
      {/* Section Header */}
      <div className="max-w-3xl mb-8 sm:mb-12 space-y-2">
        <span className="font-mono text-xs uppercase tracking-widest text-fg-muted block">
          {bento.subHeading}
        </span>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-fg text-balance">
          {bento.mainHeading}
        </h2>
      </div>

      {/* Asymmetric 3-Column Bento Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6">
        {/* Card 1: Core Application Workflow (2 cols) */}
        <Card
          surface="2"
          className="md:col-span-2 border-border p-6 sm:p-8 flex flex-col justify-between min-h-[320px]"
        >
          <div className="space-y-2 max-w-xl">
            <span className="font-mono text-xs uppercase tracking-wider text-fg-muted">
              // {bento.workflow.badge}
            </span>
            <h3 className="text-xl sm:text-2xl font-bold text-fg">
              {bento.workflow.title}
            </h3>
            <p className="text-sm text-fg-subtle leading-relaxed">
              {bento.workflow.description}
            </p>
          </div>

          {/* Workflow Simulation Bar */}
          <div className="mt-6 pt-4 border-t border-border-subtle space-y-3">
            <div className="flex items-center justify-between font-mono text-xs text-fg-muted">
              <span className="flex items-center gap-1.5 text-fg font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
                {bento.workflow.componentLabel}
              </span>
              <span>Execution Pipeline: Active</span>
            </div>

            <div className="grid grid-cols-3 gap-2 font-mono text-[11px]">
              <div className="p-2.5 rounded-xs bg-surface-1 border border-border-subtle flex items-center justify-between">
                <span className="text-fg-subtle">01 Ingestion</span>
                <CheckCircle2 className="w-3.5 h-3.5 text-success" />
              </div>
              <div className="p-2.5 rounded-xs bg-surface-1 border border-border-subtle flex items-center justify-between">
                <span className="text-fg-subtle">02 Local Validate</span>
                <CheckCircle2 className="w-3.5 h-3.5 text-success" />
              </div>
              <div className="p-2.5 rounded-xs bg-surface-1 border border-border-subtle flex items-center justify-between">
                <span className="text-fg-subtle">03 State Sync</span>
                <ArrowRight className="w-3.5 h-3.5 text-fg-muted" />
              </div>
            </div>
          </div>
        </Card>

        {/* Card 2: Execution Latency / Architecture Metric (1 col) */}
        <Card
          surface="2"
          className="md:col-span-1 border-border p-6 sm:p-8 flex flex-col justify-between min-h-[320px]"
        >
          <div className="space-y-2">
            <span className="font-mono text-xs uppercase tracking-wider text-fg-muted">
              // {bento.metric.badge}
            </span>
            <h3 className="text-xl font-bold text-fg">{bento.metric.title}</h3>
            <p className="text-xs sm:text-sm text-fg-subtle leading-relaxed">
              {bento.metric.description}
            </p>
          </div>

          <div className="py-4 my-auto">
            <div className="text-5xl sm:text-6xl font-black text-fg tracking-tight font-mono">
              {bento.metric.value}
            </div>
            <p className="font-mono text-xs text-fg-muted mt-2">{bento.metric.subtext}</p>
          </div>
        </Card>

        {/* Card 3: Hardware I/O & Connectivity Gateways (1 col) */}
        <Card
          surface="2"
          className="md:col-span-1 border-border p-6 sm:p-8 flex flex-col justify-between min-h-[320px]"
        >
          <div className="space-y-2">
            <span className="font-mono text-xs uppercase tracking-wider text-fg-muted">
              // {bento.connectivity.badge}
            </span>
            <h3 className="text-xl font-bold text-fg">{bento.connectivity.title}</h3>
            <p className="text-xs sm:text-sm text-fg-subtle leading-relaxed">
              {bento.connectivity.description}
            </p>
          </div>

          <div className="border-t border-border-subtle pt-4 space-y-2 font-mono text-xs">
            {bento.connectivity.gateways.map((gw) => (
              <div
                key={gw.name}
                className="flex items-center justify-between p-2 rounded-xs bg-surface-1 border border-border-subtle"
              >
                <span className="text-fg truncate mr-2">{gw.name}</span>
                <span className="shrink-0 flex items-center gap-1.5 text-[10px] text-fg-muted uppercase font-semibold">
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${
                      gw.status === 'OPERATIONAL'
                        ? 'bg-success'
                        : gw.status === 'COMPLIANT'
                          ? 'bg-info'
                          : 'bg-warning'
                    }`}
                  />
                  {gw.status}
                </span>
              </div>
            ))}
          </div>
        </Card>

        {/* Card 4: Data Intelligence & Reporting Telemetry (2 cols) */}
        <Card
          surface="2"
          className="md:col-span-2 border-border p-6 sm:p-8 flex flex-col justify-between min-h-[320px]"
        >
          <div className="space-y-2 max-w-xl">
            <span className="font-mono text-xs uppercase tracking-wider text-fg-muted">
              // {bento.telemetry.badge}
            </span>
            <h3 className="text-xl sm:text-2xl font-bold text-fg">
              {bento.telemetry.title}
            </h3>
            <p className="text-sm text-fg-subtle leading-relaxed">
              {bento.telemetry.description}
            </p>
          </div>

          {/* Telemetry Bar Visualization */}
          <div className="mt-6 pt-4 border-t border-border-subtle">
            <div className="h-32 sm:h-36 w-full flex items-end gap-2 sm:gap-3 px-3 sm:px-6 pt-4 pb-2 border border-border bg-surface-1 rounded-sm">
              {bento.telemetry.bars.map((barHeight, idx) => (
                <div
                  key={idx}
                  className="w-full flex flex-col items-center justify-end h-full group relative"
                >
                  <div
                    style={{ height: `${barHeight}%` }}
                    className="w-full bg-fg/20 group-hover:bg-fg/50 rounded-xs transition-all duration-200"
                  />
                  <span className="opacity-0 group-hover:opacity-100 absolute -top-6 font-mono text-[10px] bg-surface-3 px-1 rounded-xs border border-border text-fg transition-opacity pointer-events-none">
                    {barHeight}%
                  </span>
                </div>
              ))}
            </div>

            <div className="font-mono text-[10px] text-fg-muted flex justify-between pt-2 px-1">
              <span>08:00</span>
              <span>11:00</span>
              <span>14:00</span>
              <span>17:00</span>
              <span>20:00</span>
              <span>23:00</span>
            </div>
          </div>
        </Card>
      </div>
    </section>
  )
}
